import { api } from "./api";
import { konfirmasi, mintaTeks } from "./dialogAplikasi";
import { rupiah } from "./statusLabels";

/** BUSINESS-98 / K221 (Owner 18 Okt 2026, "97 dan 98 setuju"): konfirmasi transfer manual — Kepala Cabang
 *  MENGETIK nominal yang terlihat di mutasi rekening (bukan menyalin pengajuan). Sama → langsung terkonfirmasi;
 *  beda → wajib alasan tertulis, Owner diberi tahu (server yang menegakkan; layar hanya menanyakan).
 *  Tunai / DOKU tidak punya mutasi rekening → langsung. Mengembalikan null bila dibatalkan. */
export function angkaNominal(teks) {
  return Number(String(teks || "").replace(/[^0-9]/g, "")) || 0;
}

/** K227 / BUSINESS-105 (Owner 26 Okt 2026, "105 setuju"): yang DICATAT sebagai pembayaran = nominal mutasi (uang yang
 *  benar-benar masuk). Kurang → sisa tetap ditagih; lebih → kelebihan tercatat. Kalimatnya menyebut akibat itu
 *  sebelum pemutus menulis alasan. */
export function kalimatSelisihMutasi(nominal, diajukan) {
  const beda = Number(nominal) - Number(diajukan);
  const akibat = beda < 0
    ? `Kekurangan ${rupiah(-beda)} tetap ditagih ke customer.`
    : `Kelebihan ${rupiah(beda)} tercatat sebagai uang masuk.`;
  return `Nominal di mutasi ${rupiah(nominal)} berbeda dari pengajuan ${rupiah(diajukan)}. `
    + `Yang dicatat sebagai pembayaran: ${rupiah(nominal)}. ${akibat} `
    + "Tulis alasannya bila tetap dikonfirmasi — Owner akan diberi tahu.";
}

/** T4: bukti transfer yang SAMA PERSIS (sidik berkas sama) sudah dipakai pembayaran lain → peringatan keras sebelum
 *  konfirmasi. Tidak memblokir: pemutus yang menilai; pengakuannya dicatat di jejak (`bukti_kembar_diakui`).
 *  Mengembalikan false bila dibatalkan, true bila lanjut (atau tidak ada kembar). */
export async function lewatiPeringatanKembar(pr) {
  if (!pr?.bukti_kembar?.length) return true;
  return konfirmasi(pr.peringatan_bukti_kembar
    || "PERINGATAN: berkas bukti transfer ini sama persis dengan bukti pembayaran lain. Cocokkan dengan mutasi rekening.",
  { judul: "Bukti transfer sama persis", ya: "Sudah saya cek di mutasi, lanjutkan", bahaya: true });
}

/** K231 / BUSINESS-109 (Owner 27 Okt 2026, "108 - 112 setuju"): kembaran SUDAH dikonfirmasi → DIBLOKIR. Kepala
 *  Cabang tidak bisa mengonfirmasi (server menolak; layar tidak menampilkan tombolnya). Owner boleh membuka blokir
 *  dengan alasan tertulis (≥ 10 huruf, dicatat di audit). Mengembalikan {} (tidak diblokir), {alasan_buka_blokir_kembar}
 *  (Owner membuka), atau null (dibatalkan / bukan Owner). */
export const MIN_ALASAN_BUKA_BLOKIR = 10;

export async function bukaBlokirKembar(pr, { isOwner = false } = {}) {
  if (!pr?.bukti_kembar_diblokir) return {};
  if (!isOwner) throw new Error(pr.kalimat_blokir_kembar
    || "Bukti transfer ini sama persis dengan bukti yang sudah dikonfirmasi. Tolak pengajuan ini.");
  const alasan = await mintaTeks(`${pr.kalimat_blokir_kembar || "Bukti transfer ini sama persis dengan bukti yang sudah dikonfirmasi."}\n\n`
    + "Tulis alasan membuka blokir (misalnya: dua transfer berbeda, sudah dicek di mutasi tanggal …). Alasan ini dicatat.",
  { judul: "Buka blokir bukti kembar (Owner)", minPanjang: MIN_ALASAN_BUKA_BLOKIR, ya: "Buka blokir dan lanjut" });
  if (!alasan) return null;
  return { alasan_buka_blokir_kembar: alasan };
}

export async function konfirmasiDenganMutasi(pr, label = "pembayaran", { isOwner = false } = {}) {
  const buka = await bukaBlokirKembar(pr, { isOwner });
  if (!buka) return null;
  if (!buka.alasan_buka_blokir_kembar && !(await lewatiPeringatanKembar(pr))) return null;
  const kembar = pr?.bukti_kembar?.length ? { bukti_kembar_diakui: true, ...buka } : {};
  if (pr?.payment_method !== "TRANSFER") {
    return api.post("/payments/confirm", { payment_record_id: pr.id, ...kembar });
  }
  const teks = await mintaTeks(
    `Ketik nominal yang terlihat di mutasi rekening untuk ${label} ini. Pengajuan: ${rupiah(pr.amount_idr)}.`,
    { judul: "Nominal di mutasi rekening", multiBaris: false, ya: "Lanjut", minPanjang: 1 });
  if (!teks) return null;
  const nominal = angkaNominal(teks);
  if (!nominal) throw new Error("Nominal mutasi belum diisi dengan angka.");
  const muatan = { payment_record_id: pr.id, nominal_mutasi: nominal, ...kembar };
  if (nominal !== Number(pr.amount_idr)) {
    const alasan = await mintaTeks(kalimatSelisihMutasi(nominal, pr.amount_idr),
      { judul: "Nominal berbeda", minPanjang: 10, ya: "Konfirmasi dengan alasan" });
    if (!alasan) return null;
    muatan.alasan_selisih = alasan;
  }
  return api.post("/payments/confirm", muatan);
}
